/**
 * QuickPhrasePicker — Quick Text Expansion macros for clinical notes.
 * Iter 68.
 *
 * Renders a small "+ Phrases" button next to any textarea. On click, a popover
 * opens with searchable, categorized clinical phrases (special tests, ROM,
 * MMT grades, osteopathic dysfunctions, chiropractic listings, etc.). Picking
 * a phrase appends its expanded text to the bound textarea, with smart
 * separators (newline or "; " depending on length).
 *
 * Macros are read from `MACROS[discipline][category]` arrays so each phrase
 * is one click for the clinician.
 *
 * Usage:
 *   <QuickPhrasePicker discipline="physiotherapy"
 *                      onPick={(text) => set('objective', appendPhrase(form.objective, text))}/>
 */
import React, { useMemo, useState } from 'react';
import { Sparkles, Search, X } from 'lucide-react';

const MACROS = {
  physiotherapy: {
    'Special tests': [
      { label: 'Hawkins-Kennedy: negative', text: 'Hawkins-Kennedy: NEGATIVE. No pain reproduced with passive flexion + IR.' },
      { label: 'Hawkins-Kennedy: positive', text: 'Hawkins-Kennedy: POSITIVE — sharp anterolateral shoulder pain reproduced with passive flexion + IR. Suggestive of subacromial impingement.' },
      { label: 'Neer\'s test', text: 'Neer\'s impingement test: positive — pain on passive overhead flexion in scapular plane.' },
      { label: 'Empty Can (Jobe)', text: 'Empty Can (Jobe) test: positive — pain + weakness in 90° abduction with IR + thumbs-down resistance. Supraspinatus tendinopathy suspected.' },
      { label: 'Speed\'s test', text: 'Speed\'s test: positive — bicipital groove pain with resisted shoulder flexion in supination. Long-head biceps tendinopathy suspected.' },
      { label: 'Apley scratch test', text: 'Apley scratch test: bilateral functional ROM screened — no significant deficit noted.' },
      { label: 'Lachman test', text: 'Lachman test: NEGATIVE — firm endpoint, no anterior tibial translation at 20-30° flexion. ACL grossly intact.' },
      { label: 'Anterior drawer (knee)', text: 'Anterior drawer test (knee): NEGATIVE — firm endpoint, no anterior translation of the tibia. ACL intact.' },
      { label: 'McMurray test', text: 'McMurray test: NEGATIVE — no click, clunk, or joint-line pain on circumduction in flexion/extension.' },
      { label: 'Thessaly test', text: 'Thessaly test (20°): NEGATIVE bilaterally — no joint-line discomfort on weight-bearing rotation.' },
      { label: 'SLR (Straight-leg raise)', text: 'SLR: positive at 45° on the affected side — reproduces shooting pain into the posterolateral calf. Suggestive of L5-S1 nerve-root irritation.' },
      { label: 'Slump test', text: 'Slump test: positive — neural tension reproduces patient\'s familiar leg symptoms with cervical flexion.' },
      { label: 'FABER (Patrick) test', text: 'FABER test: positive on the affected side — reproduces groin pain. Suggestive of hip-joint or SI involvement.' },
      { label: 'FADIR test', text: 'FADIR test: positive — anterior groin pain on hip flexion + adduction + IR. Suggestive of femoroacetabular impingement (CAM/Pincer).' },
      { label: 'Spurling\'s test', text: 'Spurling\'s test: positive on the affected side — radicular reproduction with axial compression + extension + ipsilateral rotation.' },
      { label: 'Phalen\'s sign', text: 'Phalen\'s sign: positive — paresthesia in median-nerve distribution within 60 s of wrist flexion. Suggestive of carpal tunnel syndrome.' },
      { label: 'Tinel\'s sign (wrist)', text: 'Tinel\'s sign at wrist: positive — paresthesia reproduced on percussion over carpal tunnel.' },
      { label: 'Drop arm test', text: 'Drop-arm test: NEGATIVE — patient maintains 90° abduction with controlled descent. No full-thickness rotator-cuff tear suspected.' },
      { label: 'Thomas test', text: 'Thomas test: positive — hip-flexor tightness ≥ 15° on the affected side.' },
      { label: 'Ober test', text: 'Ober test: positive — ITB tightness limits passive hip adduction in side-lying.' },
    ],
    'ROM (Range of Motion)': [
      { label: 'Cervical AROM full', text: 'Cervical AROM: full and pain-free in all 6 planes.' },
      { label: 'Cervical AROM restricted', text: 'Cervical AROM: restricted in {{ROTATION}} ({{XX}}°/80°) with pain at end-range.' },
      { label: 'Shoulder AROM symmetric', text: 'Bilateral shoulder AROM symmetric and full: flexion 170°, abduction 170°, IR L1, ER 90°.' },
      { label: 'Knee AROM 0-130', text: 'Knee AROM: 0–130° flexion, pain-free, no end-feel restriction.' },
      { label: 'Lumbar flexion limited', text: 'Lumbar flexion: limited to {{XX}}% of expected (fingertips to {{YY}}); pain at end-range.' },
      { label: 'Hip IR/ER bilateral', text: 'Hip IR/ER bilateral: R 30°/45°, L 35°/40°, no end-range pain.' },
    ],
    'Manual Muscle Test (MMT)': [
      { label: '5/5 all key muscles', text: 'MMT: 5/5 throughout the upper and lower extremity major muscle groups. No focal weakness.' },
      { label: '4/5 weakness', text: 'MMT: {{MUSCLE}} {{4/5}} on the affected side — slight weakness against resistance.' },
      { label: 'Grading scale reference', text: 'MMT scale: 5 = full ROM against gravity + maximal resistance; 4 = full ROM against gravity + moderate resistance; 3 = full ROM against gravity only; 2 = full ROM gravity-eliminated; 1 = trace contraction; 0 = no contraction.' },
    ],
    'Posture / Gait': [
      { label: 'Posture: forward head', text: 'Posture: forward head + protracted scapula + thoracic kyphosis noted. Anterior pelvic tilt evident.' },
      { label: 'Antalgic gait', text: 'Antalgic gait on the affected side with shortened stance phase.' },
      { label: 'Trendelenburg sign', text: 'Trendelenburg sign: positive on single-leg stance, indicating gluteus medius weakness.' },
      { label: 'Normal gait', text: 'Gait: normal heel-strike to toe-off, symmetric cadence, no antalgic features.' },
    ],
    'Modalities (Plan)': [
      { label: 'Manual therapy + Exercise', text: 'Manual therapy (soft tissue + grade III mobilizations) + structured exercise prescription. Frequency 2×/week × 4 weeks, reassess.' },
      { label: 'Ultrasound 1.0 W/cm²', text: 'Therapeutic ultrasound 1.0 MHz at 1.0 W/cm², continuous, 5 min over target area.' },
      { label: 'TENS for pain', text: 'TENS — conventional mode (80–100 Hz, sub-motor intensity) × 20 min for analgesic effect.' },
      { label: 'Dry needling', text: 'Dry needling to active trigger points in {{MUSCLE}} — 3 needle sites, 5 min retention, twitch response elicited.' },
      { label: 'IFC (Interferential)', text: 'Interferential current — 4-pole placement at 80–100 Hz × 15 min for pain relief.' },
    ],
    'Outcome measures': [
      { label: 'Pain VAS', text: 'Pain VAS: {{X}}/10 at rest, {{Y}}/10 with activity.' },
      { label: 'Oswestry score', text: 'Oswestry Disability Index: {{XX}}% — {{minimal|moderate|severe|crippled|bed-bound}} disability.' },
      { label: 'QuickDASH score', text: 'QuickDASH score: {{XX}} — {{mild|moderate|severe}} upper-limb functional limitation.' },
      { label: 'NDI score', text: 'Neck Disability Index: {{XX}}% — {{mild|moderate|severe|complete}} neck-related disability.' },
    ],
  },
  osteopathy: {
    'TART findings': [
      { label: 'T8 ERSr', text: 'T8 — somatic dysfunction: ERSr (Extended, Rotated, Side-bent Right). Hypertonic right paraspinals; tenderness on right T8 transverse process; rotational asymmetry confirmed in flexion + neutral.' },
      { label: 'L4 FRSl', text: 'L4 — somatic dysfunction: FRSl (Flexed, Rotated, Side-bent Left). Restricted to right side-bending; tenderness on left L4 transverse process.' },
      { label: 'OA dysfunction', text: 'OA — somatic dysfunction: flexed, side-bent left, rotated right. Tissue texture change noted suboccipitally; cranial accessory motion restricted.' },
      { label: 'Sacrum (L-on-L torsion)', text: 'Sacrum: Left-on-Left forward torsion — deep right sulcus, deep left ILA, positive seated flexion test on the right.' },
      { label: 'Innominate posterior', text: 'Innominate: posterior rotation on the {{R|L}} side. Positive standing flexion test on the same side.' },
    ],
    'Treatment techniques': [
      { label: 'MET (Muscle Energy)', text: 'Muscle Energy Technique to {{SEGMENT}} — 3 repetitions of 5 s isometric contraction at 20% MVC, followed by re-positioning to new barrier.' },
      { label: 'MFR (Myofascial Release)', text: 'Indirect myofascial release applied to {{REGION}} — held until tissue softening and restoration of motion in all 3 planes.' },
      { label: 'HVLA thrust', text: 'HVLA thrust to {{SEGMENT}} — single high-velocity, low-amplitude thrust through restrictive barrier with audible joint cavitation.' },
      { label: 'Counterstrain', text: 'Strain/counterstrain — tender point at {{LOCATION}} treated by positioning in 90 s of relative ease.' },
      { label: 'Cranial OMT', text: 'Cranial osteopathic treatment — CV4 hold to 5-second still-point.' },
    ],
  },
  chiropractic: {
    'Listings / Subluxations': [
      { label: 'C5 PRS', text: 'C5: PRS (Posterior, Rotated Right, Superior). Motion palpation reveals restriction in left rotation; positive Otz.' },
      { label: 'L5 PL', text: 'L5: PL (Posterior, Left). Restricted right rotation; tender on left L5 mammillary process.' },
      { label: 'Sacrum P-R', text: 'Sacrum: P-R (Posterior, Right). Right sacral base posterior; positive Derifield comparison on right.' },
      { label: 'Ilium PI', text: '{{R|L}} Ilium: PI (Posterior, Inferior). Apparent functional leg length deficiency on Derifield.' },
      { label: 'AS Occiput', text: 'Occiput: AS (Anterior, Superior). Restricted flexion + ipsilateral side-bending of OA.' },
    ],
    'Adjustments': [
      { label: 'Diversified Cervical', text: 'Diversified rotational adjustment to C5 segment — supine, contact on right articular pillar; audible cavitation, immediate improvement in left rotation.' },
      { label: 'Toggle Recoil (HIO)', text: 'Toggle recoil (Hole-In-One) — single thrust on atlas, headpiece engaged; patient reports immediate symptom relief.' },
      { label: 'Drop-table SI', text: 'Drop-table (Thompson) adjustment to {{R|L}} SI joint — single drop, no audible release but motion restored on post-check.' },
      { label: 'Activator', text: 'Activator instrument adjustment to {{SEGMENT}} — single pulse, force level {{N}}, patient tolerated well.' },
    ],
  },
  massage_therapy: {
    'Tissue findings': [
      { label: 'Trigger point upper trap', text: 'Trigger point identified in upper trapezius (TrP1) — taut band with referred pain pattern to ipsilateral temple.' },
      { label: 'Fascial restriction lumbodorsal', text: 'Fascial restriction in lumbodorsal fascia — palpable thickening + reduced glide; limits trunk rotation.' },
      { label: 'Adhesion gastrocnemius', text: 'Adhesion noted at the medial head of gastrocnemius — tender on cross-fiber palpation.' },
      { label: 'Hypertonicity', text: 'Hypertonicity noted in {{MUSCLE}} bilaterally; tone reduced post-treatment.' },
    ],
    'Techniques': [
      { label: 'Swedish (Effleurage)', text: 'Swedish effleurage applied to {{REGION}} × 10 min — patient reports immediate relaxation.' },
      { label: 'Deep tissue', text: 'Deep tissue work applied to {{REGION}} — slow longitudinal strokes following muscle fiber direction.' },
      { label: 'Trigger point release', text: 'Sustained trigger point release on {{MUSCLE}} × 90 s; referred pain pattern abolished.' },
      { label: 'Myofascial release', text: 'Myofascial release to {{REGION}} — sustained pressure with tissue listening until release felt.' },
      { label: 'Lymphatic drainage', text: 'Manual lymphatic drainage applied to {{REGION}} — light pumping strokes following lymph flow direction.' },
    ],
  },
};

/** Appends `phrase` to existing `current`, adding a newline if `current` is non-empty. */
export function appendPhrase(current, phrase) {
  const c = (current || '').trim();
  if (!c) return phrase;
  return `${c}\n${phrase}`;
}

export default function QuickPhrasePicker({ discipline = 'physiotherapy', onPick, testid = 'quick-phrase-picker', label = 'Phrases' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const lib = MACROS[discipline] || MACROS.physiotherapy;

  const filtered = useMemo(() => {
    if (!q.trim()) return lib;
    const needle = q.toLowerCase();
    const out = {};
    Object.entries(lib).forEach(([cat, items]) => {
      const hits = items.filter(m =>
        m.label.toLowerCase().includes(needle) ||
        m.text.toLowerCase().includes(needle)
      );
      if (hits.length) out[cat] = hits;
    });
    return out;
  }, [lib, q]);

  if (!open) {
    return (
      <button type="button"
              onClick={() => setOpen(true)}
              data-testid={`${testid}-open`}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-soft text-fg-muted hover:border-brand hover:text-brand transition">
        <Sparkles className="w-3 h-3"/> {label}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-20 px-4 bg-black/40" onClick={() => setOpen(false)} data-testid={`${testid}-modal`}>
      <div className="bg-bg-primary border border-soft rounded-xl w-full max-w-2xl max-h-[75vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold text-sm">Quick phrase library · <span className="capitalize text-fg-muted">{discipline.replace('_', ' ')}</span></h3>
          </div>
          <button onClick={() => setOpen(false)} className="text-fg-muted hover:text-fg" data-testid={`${testid}-close`}>
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-3 border-b border-soft">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"/>
            <input autoFocus
                   placeholder="Search phrases (e.g. Hawkins, SLR, ROM…)"
                   value={q}
                   onChange={(e) => setQ(e.target.value)}
                   className="input pl-9 !text-sm"
                   data-testid={`${testid}-search`}/>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {Object.keys(filtered).length === 0 ? (
            <div className="text-center text-fg-muted text-sm py-8">No phrases match "{q}".</div>
          ) : (
            Object.entries(filtered).map(([cat, items]) => (
              <div key={cat}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-fg-muted mb-1.5">{cat}</div>
                <div className="grid grid-cols-1 gap-1.5">
                  {items.map((m) => (
                    <button key={m.label} type="button"
                            onClick={() => { onPick?.(m.text); setOpen(false); }}
                            data-testid={`${testid}-pick-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`}
                            className="text-left p-2.5 rounded-md border border-soft hover:border-brand hover:bg-brand/5 transition group">
                      <div className="text-sm font-medium text-fg group-hover:text-brand">{m.label}</div>
                      <div className="text-[11px] text-fg-muted line-clamp-2 mt-0.5">{m.text}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="p-3 border-t border-soft text-[11px] text-fg-muted text-center">
          Placeholders like <code className="text-brand">{`{{MUSCLE}}`}</code> or <code className="text-brand">{`{{XX}}`}</code> are intentional — replace them inline once inserted.
        </div>
      </div>
    </div>
  );
}
